import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './hooks/useAuth';
import { User } from './types';
import { login as apiLogin, getMe } from './api/auth';
import { Layout } from './components/layout/Layout';
import { Login } from './pages/auth/Login';
import { SuperadminDashboard } from './pages/superadmin/Dashboard';
import { BookingsAdmin } from './pages/superadmin/BookingsAdmin';
import { HotelDashboard } from './pages/hotel/Dashboard';
import { AgentDashboard } from './pages/agent/Dashboard';
import { SearchBook } from './pages/agent/SearchBook';
import { MarkupRules } from './pages/agent/MarkupRules';
import { AgentBookings } from './pages/agent/Bookings';
import { AgentWallet } from './pages/agent/Wallet';
import { CustomerBookings } from './pages/customer/Bookings';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('access_token');
  if (!token) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};


export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('access_token'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (token) {
      getMe().then(r => { setUser(r.data); setIsLoading(false); })
             .catch(() => { localStorage.clear(); setToken(null); setIsLoading(false); });
    } else { setIsLoading(false); }
  }, [token]);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await apiLogin(email, password);
    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    setToken(data.access);
    setUser(data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.clear();
    setToken(null);
    setUser(null);
  }, []);

  const getHomePath = (role: string) => {
    if (role === 'system') return '/superadmin';
    if (role === 'hotel') return '/hotel';
    if (['admin_agent', 'sub_agent', 'direct_agent'].includes(role)) return '/agent';
    return '/customer/bookings';
  };

  if (isLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui', color: '#888780' }}>Memuat...</div>;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to={getHomePath(user.role)} />} />
          <Route path="/" element={<Navigate to={user ? getHomePath(user.role) : '/login'} />} />

          <Route path="/superadmin" element={<ProtectedRoute><SuperadminDashboard /></ProtectedRoute>} />
          <Route path="/superadmin/bookings" element={<ProtectedRoute><BookingsAdmin /></ProtectedRoute>} />
          <Route path="/superadmin/hotels" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Hotels (coming soon)</h1></div></ProtectedRoute>} />
          <Route path="/superadmin/users" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Users (coming soon)</h1></div></ProtectedRoute>} />
          <Route path="/superadmin/wallets" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Wallets (coming soon)</h1></div></ProtectedRoute>} />
          <Route path="/superadmin/audit" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Audit (coming soon)</h1></div></ProtectedRoute>} />

          <Route path="/hotel" element={<ProtectedRoute><HotelDashboard /></ProtectedRoute>} />
          <Route path="/hotel/bookings" element={<ProtectedRoute><HotelDashboard /></ProtectedRoute>} />
          <Route path="/hotel/inventory" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Inventory (coming soon)</h1></div></ProtectedRoute>} />
          <Route path="/hotel/rates" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Rates (coming soon)</h1></div></ProtectedRoute>} />
          <Route path="/hotel/wallet" element={<ProtectedRoute><AgentWallet /></ProtectedRoute>} />

          <Route path="/agent" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />
          <Route path="/agent/search" element={<ProtectedRoute><SearchBook /></ProtectedRoute>} />
          <Route path="/agent/bookings" element={<ProtectedRoute><AgentBookings /></ProtectedRoute>} />
          <Route path="/agent/markup" element={<ProtectedRoute><MarkupRules /></ProtectedRoute>} />
          <Route path="/agent/wallet" element={<ProtectedRoute><AgentWallet /></ProtectedRoute>} />
          <Route path="/agent/sub-agents" element={<ProtectedRoute><div style={{ padding: 24 }}><h1>Sub-Agents (coming soon)</h1></div></ProtectedRoute>} />

          <Route path="/customer/bookings" element={<ProtectedRoute><CustomerBookings /></ProtectedRoute>} />
          <Route path="/customer/wallet" element={<ProtectedRoute><AgentWallet /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
